import React, { useEffect, useState } from 'react';
import { evictionService } from '../../services/api';

interface Ground {
    code: string;
    name: string;
    description: string;
    category: 'mandatory' | 'discretionary';
    noticePeriodMonth: number;
    protectedPeriodMonth?: number;
    rraChanges?: string;
}

interface GroundSelectorProps {
    onSelect: (ground: Ground) => void;
    selectedGroundCode?: string;
    tenancyStartDate?: string; // Used for protected period validation
}

export default function GroundSelector({ onSelect, selectedGroundCode, tenancyStartDate }: GroundSelectorProps) {
    const [grounds, setGrounds] = useState<Ground[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchGrounds = async () => {
            try {
                const response = await evictionService.getGrounds();
                setGrounds(response.data.data || []);
            } catch (err) {
                console.error("Failed to fetch grounds", err);
                setError("Could not load eviction grounds.");
            } finally {
                setLoading(false);
            }
        };
        fetchGrounds();
    }, []);

    const isDisabled = (ground: Ground) => {
        if (!ground.protectedPeriodMonth || !tenancyStartDate) return false;

        const start = new Date(tenancyStartDate);
        const protectedUntil = new Date(start);
        protectedUntil.setMonth(start.getMonth() + ground.protectedPeriodMonth);

        return new Date() < protectedUntil;
    };

    if (loading) return <div className="p-4 text-center">Loading legal grounds...</div>;
    if (error) return <div className="p-4 text-red-600">{error}</div>;

    const mandatoryGrounds = grounds.filter(g => g.category === 'mandatory');
    const discretionaryGrounds = grounds.filter(g => g.category === 'discretionary');

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Select Grounds for Possession</h3>
                <p className="text-sm text-gray-500 mb-4">
                    Under the Renters' Rights Act 2025, Section 21 is abolished. You must select a valid Section 8 ground.
                </p>

                {/* Mandatory Grounds */}
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                <strong>Mandatory Grounds:</strong> If proven, the court <em>must</em> grant possession.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {mandatoryGrounds.map((ground) => {
                        const disabled = isDisabled(ground);
                        return (
                            <div
                                key={ground.code}
                                className={`relative rounded-lg border p-4 shadow-sm flex flex-col cursor-pointer hover:border-indigo-500 focus:outline-none 
                                    ${selectedGroundCode === ground.code ? 'border-indigo-500 ring-2 ring-indigo-500 bg-indigo-50' : 'border-gray-300'}
                                    ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}
                                `}
                                onClick={() => !disabled && onSelect(ground)}
                            >
                                <div className="flex-1">
                                    <div className="flex justify-between">
                                        <h4 className="text-sm font-medium text-gray-900">{ground.name}</h4>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                            {ground.code.replace('_', ' ')}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-sm text-gray-500">{ground.description}</p>

                                    {ground.rraChanges && (
                                        <p className="mt-2 text-xs text-indigo-600 font-medium">
                                            <i className="fas fa-info-circle mr-1"></i>
                                            RRA 2025: {ground.rraChanges}
                                        </p>
                                    )}

                                    {disabled && (
                                        <p className="mt-2 text-xs text-red-600 font-bold">
                                            Cannot use until {new Date(new Date(tenancyStartDate!).setMonth(new Date(tenancyStartDate!).getMonth() + ground.protectedPeriodMonth!)).toLocaleDateString()}
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Discretionary Grounds */}
            <div className="mt-8">
                <h4 className="text-md font-medium text-gray-900 mb-2">Discretionary Grounds</h4>
                <p className="text-sm text-gray-500 mb-4">The court will decide if eviction is reasonable.</p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {discretionaryGrounds.map((ground) => (
                        <div
                            key={ground.code}
                            className={`relative rounded-lg border p-4 shadow-sm flex flex-col cursor-pointer hover:border-indigo-500 focus:outline-none 
                                ${selectedGroundCode === ground.code ? 'border-indigo-500 ring-2 ring-indigo-500 bg-indigo-50' : 'border-gray-300'}
                            `}
                            onClick={() => onSelect(ground)}
                        >
                            <div className="flex-1">
                                <div className="flex justify-between">
                                    <h4 className="text-sm font-medium text-gray-900">{ground.name}</h4>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                        {ground.code.replace('_', ' ')}
                                    </span>
                                </div>
                                <p className="mt-2 text-sm text-gray-500">{ground.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
